import {
  Box,
  Button,
  Card,
  CircularProgressBar,
  Image,
  Loader,
  MarketingPageLayout,
  MarketingPageLayoutContent,
  Notification,
  Page,
  WixDesignSystemProvider,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import * as Icons from "@wix/wix-ui-icons-common";
import React, { useEffect } from "react";
import { ReactTagManager } from "react-gtm-ts";
import Cc from "./cc.png";
import { useTranslation } from "react-i18next";
import { identify, track, WIX_APP_ID } from "./analytics";

const tagManagerArgs = {
  code: "GTM-WZQPMC7",
};

const BASE_URL = `https://wix.certifiedcode.io/circular/_functions`;

function App() {
  const { t } = useTranslation();
  const token = new URLSearchParams(window.location.search).get("token");
  const instance = new URLSearchParams(window.location.search).get("instance");
  const [isUpgraded, setIsUpgraded] = React.useState(false);
  const [trialRemainingDate, setTrialRemainingDate] = React.useState(0);
  const [instanceData, setInstanceData] = React.useState({
    instance: {
      isFree: true,
      availablePlans: [],
      instanceId: "",
    },
  });

  const isFree = instanceData?.instance?.isFree !== false;
  const [isUpgradeBannerOpen, setIsUpgradeBannerOpen] =
    React.useState<boolean>(isFree);

  useEffect(() => {
    ReactTagManager.init(tagManagerArgs);
  }, []);

  useEffect(() => {
    identify(instance);
    track("dashboard_opened", { has_token: Boolean(token) });
    setInstance();
    getTrial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, token]);

  function setInstance() {
    fetch(BASE_URL + "/@certifiedcode/base-backend/instance", {
      method: "GET",
      headers: {
        Authorization: instance || "",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setInstanceData(data);
        const upgraded = data.instance.isFree === false;
        setIsUpgraded(upgraded);
        setIsUpgradeBannerOpen(!upgraded);
      })
      .catch(() => {});
  }

  function getTrial() {
    fetch(`${BASE_URL}/premium`, {
      headers: {
        Authorization: instance || "",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setTrialRemainingDate(data?.trial);
      })
      .catch(() => {});
  }

  if (token) {
    track("install_redirect");
    window.location.href = `https://www.wix.com/installer/install?token=${token}&appId=${WIX_APP_ID}&redirectUrl=https://wix.certifiedcode.io/circular/_functions/@certifiedcode/base-backend/auth`;
    return (
      <WixDesignSystemProvider features={{ newColorsBranding: true }}>
        <Page height="100vh">
          <Page.Content>
            <Box height={"90vh"} direction="vertical" verticalAlign="middle">
              <Loader text={t("loading")} />
            </Box>
          </Page.Content>
        </Page>
      </WixDesignSystemProvider>
    );
  }

  const upgradeUrl = `https://memberships.certifiedcode.us/${window.location.search}`;

  return (
    <WixDesignSystemProvider features={{ newColorsBranding: true }}>
      <Notification theme="premium" show={isUpgradeBannerOpen} type={"sticky"}>
        <Notification.TextLabel>{t("upgrade-title")}</Notification.TextLabel>
        <Notification.ActionButton
          type="textLink"
          link={upgradeUrl}
          target="_blank"
          onClick={() => {
            track("upgrade_clicked", { source: "banner" });
          }}
        >
          {t("upgrade-button")}
        </Notification.ActionButton>
        <Notification.CloseButton
          className="close-button"
          onClick={() => setIsUpgradeBannerOpen(false)}
        />
      </Notification>
      <Card>
        <MarketingPageLayout
          removeImageHorizontalPadding
          removeImageVerticalPadding
          content={
            <Box height="840px" verticalAlign="middle" direction="vertical">
              <MarketingPageLayoutContent
                title={t("circular-title")}
                content={t("circular-content")}
                actions={
                  <Box direction="vertical" gap="SP2">
                    <Card>
                      <Card.Content>
                        <CircularProgressBar
                          label={
                            isUpgraded
                              ? t("premium-msg")
                              : t("premium-update", { trialRemainingDate })
                          }
                          skin={isUpgraded ? "standard" : "premium"}
                          showProgressIndication
                          value={isUpgraded ? 100 : 1 - trialRemainingDate / 7}
                        />
                      </Card.Content>
                    </Card>
                    <Button
                      skin="premium"
                      prefixIcon={<Icons.PremiumFilled />}
                      disabled={isUpgraded}
                      as="a"
                      href={upgradeUrl}
                      target="_blank"
                      onClick={() => track("upgrade_clicked", { source: "cta" })}
                    >
                      {isUpgraded ? t("manage-plan-button") : t("upgrade-premium-button")}
                    </Button>
                    <Button
                      priority="secondary"
                      prefixIcon={<Icons.GetStarted />}
                      onClick={() => {
                        track("setup_guide_clicked");
                        window.Intercom("showArticle", 8678985);
                      }}
                    >
                      {t("setup_guide")}
                    </Button>
                    <Button
                      skin="inverted"
                      prefixIcon={<Icons.Reviews />}
                      as="a"
                      href={`https://bridget.reviews.certifiedcode.us/${WIX_APP_ID}${window.location.search}`}
                      target="_blank"
                      onClick={() => track("write_review_clicked")}
                    >
                      {t("circular-rate-button")}
                    </Button>
                  </Box>
                }
              />
            </Box>
          }
          image={<Image borderRadius={0} src={Cc} />}
        />
      </Card>
    </WixDesignSystemProvider>
  );
}

export default App;
